import { FormEvent, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ErrorDialog from '@/components/ErrorDialog'
import Seo from '@/components/Seo'
import { useAuth } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'

type Mode = 'signin' | 'signup' | 'reset'

function authErrorMessage(error: unknown) {
  const code = (error as { data?: { code?: number } })?.data?.code
  const map: Record<number, string> = {
    2254: 'El registro público no está disponible en este momento.',
    2259: 'La cuenta no está activa.',
    2261: 'El acceso con correo no está activo.',
    2262: 'El acceso con Google no está activo.',
  }
  if (code && map[code]) return map[code]
  return 'No se pudo completar la autenticación. Revisa los datos e inténtalo de nuevo.'
}

function AuthPage() {
  const { isAuthenticated } = useAuth()
  const [params] = useSearchParams()
  const next = params.get('next') || '/panel'
  const navigate = useNavigate()
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [userName, setUserName] = useState('')
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isAuthenticated) navigate(next, { replace: true })
  }, [isAuthenticated, navigate, next])

  useEffect(() => {
    if (countdown <= 0) return undefined
    const timer = setTimeout(() => setCountdown((value) => value - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown])

  const switchMode = (value: Mode) => {
    setMode(value)
    setOtp('')
    setOtpSent(false)
    setError('')
  }

  const sendOtp = async () => {
    if (!email) {
      setError('Introduce un correo electrónico.')
      return
    }
    try {
      await lumi.auth.sendOtp({ email, type: mode === 'reset' ? 'reset_password' : 'register' })
      setOtpSent(true)
      setCountdown(60)
    } catch (err) {
      setError(authErrorMessage(err))
    }
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setLoading(true)
    try {
      if (mode === 'signin') {
        await lumi.auth.signInWithPassword({ email, password })
      } else if (mode === 'signup') {
        await lumi.auth.signUpWithPassword({ email, password, otp, userName })
      } else {
        await lumi.auth.resetPassword({ email, password, otp })
      }
      window.location.href = next
    } catch (err) {
      setError(authErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const google = async () => {
    try {
      await lumi.auth.signInWithOAuth({ provider: 'google' })
      window.location.href = next
    } catch (err) {
      setError(authErrorMessage(err))
    }
  }

  return (
    <>
      <Seo title="Acceso" description="Inicia sesión o crea tu cuenta en HFCursos para inscribirte y seguir tu progreso." />
      {error ? <ErrorDialog message={error} onClose={() => setError('')} /> : null}
      <section
        className="relative isolate overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('https://static.lumi.new/7e/7e2bc1029eebc0856e77c1ed87516a4c.png')" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#F4EFE6]/95 via-[#F4EFE6]/90 to-[#F4EFE6]/95" aria-hidden="true" />
        <div className="relative mx-auto max-w-md px-5 py-16">
        <p className="text-xs uppercase tracking-[0.28em] text-[#C41E3A]">Cuenta</p>
        <h1 className="mt-3 font-['Fraunces'] text-4xl">
          {mode === 'signin' ? 'Acceder' : mode === 'signup' ? 'Registrarse' : 'Restablecer clave'}
        </h1>
        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          {mode === 'signup' ? (
            <label className="block text-sm">
              Nombre
              <input
                value={userName}
                onChange={(event) => setUserName(event.target.value)}
                className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2"
              />
            </label>
          ) : null}
          <label className="block text-sm">
            Correo
            <input
              required
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2"
            />
          </label>
          {mode !== 'signin' ? (
            <div className="flex gap-2">
              <label className="block flex-1 text-sm">
                Código OTP
                <input
                  value={otp}
                  onChange={(event) => setOtp(event.target.value)}
                  className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2"
                />
              </label>
              <button
                type="button"
                onClick={sendOtp}
                disabled={countdown > 0}
                className="mt-6 h-10 shrink-0 border border-[#1E3A5F] px-3 text-xs disabled:opacity-40"
              >
                {countdown > 0 ? `${countdown}s` : 'Enviar OTP'}
              </button>
            </div>
          ) : null}
          <label className="block text-sm">
            Contraseña
            <input
              required
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              className="mt-1 w-full border border-[#1E3A5F]/20 bg-transparent px-3 py-2"
            />
          </label>
          <button
            type="submit"
            disabled={loading || (mode !== 'signin' && !otpSent)}
            className="w-full bg-[#1E3A5F] py-2.5 text-sm text-[#F4EFE6] disabled:opacity-40"
          >
            {loading ? 'Un momento…' : mode === 'signin' ? 'Entrar' : mode === 'signup' ? 'Crear cuenta' : 'Guardar clave'}
          </button>
        </form>
        {mode === 'signin' ? (
          <button type="button" onClick={google} className="mt-3 w-full border border-[#1E3A5F]/20 py-2.5 text-sm">
            Continuar con Google
          </button>
        ) : null}
        <div className="mt-6 space-y-2 text-sm text-[#1E3A5F]/70">
          {mode === 'signin' ? (
            <>
              <button type="button" className="underline" onClick={() => switchMode('reset')}>
                Olvidé la contraseña
              </button>
              <p>
                ¿Sin cuenta?{' '}
                <button type="button" className="underline" onClick={() => switchMode('signup')}>
                  Registrarse
                </button>
              </p>
            </>
          ) : (
            <p>
              ¿Ya tienes cuenta?{' '}
              <button type="button" className="underline" onClick={() => switchMode('signin')}>
                Acceder
              </button>
            </p>
          )}
        </div>
        </div>
      </section>
    </>
  )
}

export default AuthPage
